import './styles.scss';
import { useVAdminSelectedIncident } from "#root/queries/local";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { useCallback, useEffect, useRef, useState } from "react";
import atlas, { AuthenticationType, ControlOptions } from "azure-maps-control";
import { AzureMap, AzureMapHtmlMarker, AzureMapsProvider, IAzureMapOptions } from "react-azure-maps";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useIncidentById, useInfiniteIncidents } from "#root/queries/incidents";
import { Drawer, DrawerBody, DrawerHeader, Dropdown, Field, Spinner, Text, Option, Button, DrawerHeaderTitle, Badge } from "@fluentui/react-components";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { StatusMapLocation, useStatusMapLocations } from "#root/queries/statusMap";
import { Dismiss16Regular } from "@fluentui/react-icons";

const MAP_STYLES = {
    GRAYSCALE: 'grayscale_light',
    ROAD_SHADED: 'road_shaded_relief',
    SATELLITE: 'satellite_road_labels'
};

declare global {
    interface Window {
        atlas: any;
    }
}

const StatusMap: React.FC = () => {
    const [selectedLocation, setSelectedLocation] = useState<StatusMapLocation | null>(null);
    const [isZooming, setIsZooming] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [popups, setPopups] = useState<Record<string, atlas.Popup>>({});
    const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
    const [currentMapStyle, setCurrentMapStyle] = useState<string>(MAP_STYLES.GRAYSCALE);
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<atlas.Map | null>(null);
    const loadingTimeoutRef = useRef<NodeJS.Timeout>();
    const [showLoading, setShowLoading] = useState(false);

    const { selectedIncidentId: vAdminIncidentId, setVAdminSelectedIncidentId } = useVAdminSelectedIncident();

    const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>();
    const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();

    const mapControls = [
        { controlName: 'ZoomControl', options: { position: 'bottom-left' } as ControlOptions },
        { controlName: 'FullscreenControl', options: { position: 'top-right' } as ControlOptions },
    ];

    const mapOptions: IAzureMapOptions = {
        authOptions: {
            authType: AuthenticationType.subscriptionKey,
            subscriptionKey: process.env.REACT_APP_AZURE_MAPS
        },
        renderWorldCopies: false,
        preserveDrawingBuffer: true,
        tileBuffer: 2,
        controls: mapControls,
        showLabels: true,
        showLogo: false,
        style: MAP_STYLES.GRAYSCALE,
        enableAccessibility: true,
        showFeedbackLink: false,
        maxZoom: 18
    };

    const {
        selectedId: selectedIncidentId,
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentQueryResult
    } = useInfiniteDropdown({
        initialId: vAdminIncidentId || '',
        onChange: (id) => {
            if (id) {
                setVAdminSelectedIncidentId(id);
                // Clear filters when switching incidents
                setSelectedLocationId(undefined);
                setSelectedGroupId(undefined);
                setSelectedLocation(null);
            }
        },
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById
    });

    // Get location name by ID for display purposes
    const getLocationNameById = (id: string | undefined): string => {
        if (!id) return '';
        const location = locations.find(item => item.location?.id === id);
        return location?.location?.name || id;
    };

    // Fetch map locations and statistics with separate filter states
    const { data: locationsData, isFetching: locationsIsFetching } = useStatusMapLocations({
        incidentId: selectedIncidentId || undefined,
        //locationId: selectedLocationId,
        groupId: selectedGroupId
    });
    const locations = locationsData?.items ?? [];

    // Get the incident location data from the locations array
    const incidentLocation = selectedIncidentId ? 
        locations.find(loc => loc.isIncident && loc.location.id === selectedIncidentId) : 
        undefined;

    const handleMarkerClick = useCallback((location: StatusMapLocation) => {
        setSelectedLocation(location);
    }, []);

    // Handle location filter change
    const handleLocationChange = (value: string) => {
        setSelectedLocationId(value || undefined);
        
        // Focus on the selected location if it has coordinates
        if (value && mapRef.current) {
            const selectedLocation = locations.find(item => item.location?.id === value);
            if (selectedLocation?.location?.latitude && selectedLocation?.location?.longitude) {
                mapRef.current.setCamera({
                    center: [selectedLocation.location.longitude, selectedLocation.location.latitude],
                    zoom: 15,
                    duration: 500
                });
                
                // Also open the location details drawer
                setSelectedLocation(selectedLocation);
            }
        }
    };

    // Handle group filter change
    const handleGroupChange = (value: string) => {
        setSelectedGroupId(value || undefined);
    };

    useEffect(() => {
        // Only show loading if we're actually fetching new locations
        if (locationsIsFetching && !locationsData) {
            setShowLoading(true);
        } else {
            loadingTimeoutRef.current = setTimeout(() => {
                setShowLoading(false);
            }, 100);
        }

        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, [locationsData, locationsIsFetching]);

    // Manage popups
    useEffect(() => {
        Object.values(popups).forEach(popup => popup.close());

        const newPopups: Record<string, atlas.Popup> = {};
        locations.forEach((item: StatusMapLocation) => {
            if (item.location?.id) {
                newPopups[item.location.id] = new atlas.Popup({
                    pixelOffset: [0, -40],
                    closeButton: false,
                    showPointer: false,
                    content: `
                            <div class="popup-content">
                             <div><span class="popup-content__title">${item.location.name}</span></div>
                             <div>
                                <span class="popup-content__label">Active Personnel: </span>
                                 <span class="popup-content__value">${item.activePersonnel}</span>
                             </div>
                            ${!item.isIncident ? `<div>
                                <span class="popup-content__label">Days Location Active: </span>
                                <span class="popup-content__value">${item.durationDays}</span>
                             </div>` : ''}
                         </div>
                        `
                });
            }
        });

        setPopups(newPopups);
        return () => Object.values(newPopups).forEach(popup => popup.close());
    }, [locations, selectedIncidentId]);

    // Reset map ready state when component unmounts
    useEffect(() => {
        const cleanup = () => {
            // Only cleanup if unmounting, not just updating
            setTimeout(() => {
                if (!document.querySelector('.map-container')) {
                    setMapReady(false);
                    mapRef.current = null;
                }
            }, 0);
        };
        return cleanup;
    }, []);

    const updateMapBounds = useCallback(() => {
        if (!mapRef.current || !locations.length) {
            return;
        }

        try {
            // Get valid points for the bounding box
            const points = locations
                .filter(item =>
                    typeof item.location?.latitude === 'number' &&
                    typeof item.location?.longitude === 'number'
                )
                .map(item => [item.location.longitude!, item.location.latitude!] as [number, number]);

            if (points.length === 0) {
                return;
            }

            // Create a bounding box that includes all points
            const bounds = atlas.data.BoundingBox.fromPositions(points);

            // Set the camera to show the bounding box
            mapRef.current.setCamera({
                bounds,
                padding: {
                    top: 100,
                    bottom: 20,
                    left: 100,
                    right: 100
                },
                duration: 500
            });
        } catch (error) {
            console.error('Error setting map bounds:', error);
        }
    }, [locations]);

    // Handle map initialization and zoom
    useEffect(() => {
        if (!mapReady || !locations.length) {
            setIsZooming(false);
            return;
        }

        const hasLocationsWithCoordinates = locations.some(item => item.location?.latitude && item.location?.longitude);
        if (hasLocationsWithCoordinates) {
            setIsZooming(true);
            updateMapBounds();

            // Reset zoom state after animation completes
            setTimeout(() => {
                setIsZooming(false);
            }, 600);
        }
    }, [mapReady, locations, selectedIncidentId, updateMapBounds]);

    // Update selected location when incident changes to get new statistics
    useEffect(() => {
        if (selectedLocation) {
            const updatedLocation = locations.find(loc => loc.location.id === selectedLocation.location.id);
            if (updatedLocation) {
                setSelectedLocation(updatedLocation);
            } else {
                setSelectedLocation(null);
            }
        }
    }, [selectedIncidentId, locations]);

    // Filter out simple locations that match incident addresses
    const filteredLocations: StatusMapLocation[] = [];
    locations.forEach(location => {
        if (location.isIncident) {
            filteredLocations.push(location);
            return;
        }

        // Check if this simple location's address matches any incident location
        const hasMatchingIncident = locations.some(incident =>
            incident.isIncident &&
            incident.location?.address?.toLowerCase() === location.location?.address?.toLowerCase()
        );

        if (!hasMatchingIncident) {
            filteredLocations.push(location);
        }
    });

    // Sort locations to keep incidents on top
    const sortedLocations = [...filteredLocations].sort((a, b) => {
        if (a.isIncident && !b.isIncident) return 1;
        if (!a.isIncident && b.isIncident) return -1;
        return 0;
    });

    return (
        <div className="base-map">
            <div className="base-map__content status-map">
                <div className="base-map__header">
                    <div className="emc-page-header">
                        <div className="emc-page-header__top-row">
                            <div className="emc-page-header__breadcrumbs">
                                <Breadcrumbs />
                            </div>
                        </div>
                        <div className="emc-page-header__title">
                            <h2>Map Victims <div className="divider" /></h2>
                        </div>
                    </div>
                    <div className="base-map__controls">
                        <Field label="Incident" className="base-map__controls--incident">
                            <InfiniteDropdown
                                value={selectedIncidentId || ''}
                                onChange={(_, data) => handleIncidentChange(data.value)}
                                onFilterChange={setIncidentFilter}
                                queryResult={incidentQueryResult}
                                itemToOption={(incident) => ({
                                    value: incident.id ?? '',
                                    text: incident.name ?? ''
                                })}
                                placeholder="Select Incident"
                                selectedItem={selectedIncidentData}
                                clearable={false}
                            />
                        </Field>

                        <Field label="Location" className="base-map__controls--location">
                            <Dropdown
                                selectedOptions={selectedLocationId ? [selectedLocationId] : []}
                                onOptionSelect={(_, data) => handleLocationChange(data.optionValue as string)}
                                disabled={!selectedIncidentId}
                                placeholder="All Locations"
                                clearable
                            >
                                {locations
                                    .filter(item => !item.isIncident)
                                    .map(item => (
                                        <Option key={item.location.id} value={item.location.id} text={item.location.name || ''}>
                                            {item.location.name || ''}
                                        </Option>
                                    ))
                                }
                            </Dropdown>
                        </Field>

                        <Field label="Group">
                            <Dropdown
                                value={selectedGroupId || ""}
                                onOptionSelect={(_, data) => handleGroupChange(data.optionValue as string)}
                                disabled={!selectedIncidentId}
                                placeholder="All Groups"
                                clearable
                            >
                            </Dropdown>
                        </Field>

                        <Badge color="success" size="extra-large">
                            Total Personnel: {incidentLocation?.activePersonnel ?? '-'}
                        </Badge>
                    </div>
                </div>

                <div className="map-container" ref={mapContainer}>
                    <AzureMapsProvider>
                        <AzureMap
                            options={mapOptions}
                            controls={mapControls}
                            events={{
                                ready: (e: atlas.Map) => {
                                    const mapInstance = (e as any).map || e;
                                    mapRef.current = mapInstance;
                                    setMapReady(true);

                                    // Listen for style changes
                                    mapInstance.events.add('styledata', () => {
                                        const style = mapInstance.getStyle().style;
                                        setCurrentMapStyle(style);
                                    });
                                }
                            }}
                        >
                            {sortedLocations
                                .filter(item => item.location?.id && item.location.latitude && item.location.longitude)
                                .map(item => {
                                    const isHovered = hoveredLocationId === item.location.id;
                                    const markerIcon = item.isIncident ? "/icons/incident-marker.svg" : "/icons/marker.svg";
                                    return (
                                        <AzureMapHtmlMarker
                                            key={item.location.id}
                                            isPopupVisible={isHovered}
                                            markerContent={
                                                <div
                                                    className={
                                                        `map-marker 
                                                                ${isHovered ? 'map-marker--hovered' : ''} 
                                                                ${!item.isIncident ? 'map-marker-location' : ''} 
                                                                ${!item.isIncident && currentMapStyle === MAP_STYLES.SATELLITE ? 'map-marker--hybrid' : ''}`
                                                    }
                                                    style={{ zIndex: item.isIncident ? 2 : 1 }}
                                                >
                                                    <img src={markerIcon} alt="location" />
                                                </div>
                                            }
                                            options={{
                                                position: [item.location.longitude!, item.location.latitude!],
                                                text: `${item.location.name}`,
                                                popup: popups[item.location.id!]
                                            }}
                                            events={[
                                                { eventName: 'click', callback: () => handleMarkerClick(item) },
                                                { eventName: 'mouseover', callback: () => setHoveredLocationId(item.location.id!) },
                                                { eventName: 'mouseout', callback: () => setHoveredLocationId(null) }
                                            ]}
                                        />
                                    );
                                })}
                        </AzureMap>
                    </AzureMapsProvider>

                    {showLoading && selectedIncidentId && (
                        <div className="loading-overlay">
                            <div className="loading-content">
                                <Spinner />
                                <Text>Loading locations...</Text>
                            </div>
                        </div>
                    )}

                    {!selectedIncidentId && (
                        <div className="loading-overlay">
                            <div className="loading-content">
                                <Text size={500} weight="semibold" align="center">
                                    Please select an incident to view the status map
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="base-map__drawer">
                <Drawer
                    className="locations-drawer status-map-drawer"
                    type="inline"
                    open={!!selectedLocation}
                    position="end"
                    onOpenChange={(_, { open }) => {
                        if (!open) {
                            setSelectedLocation(null);
                            setSelectedLocationId(undefined);
                        }
                    }}
                >
                    <DrawerHeader className="status-map-drawer__header">
                        <DrawerHeaderTitle
                            action={
                                <Button
                                    appearance="subtle"
                                    aria-label="Close"
                                    icon={<Dismiss16Regular />}
                                    onClick={() => {
                                        setSelectedLocation(null);
                                        setSelectedLocationId(undefined);
                                    }}
                                />
                            }
                        >
                            {selectedIncidentData && (
                                <div className="incident-info-header">
                                    <div className="incident-info-title">
                                        {selectedIncidentData.name}
                                    </div>
                                    {incidentLocation && (
                                        <>
                                            <div className="incident-info-row">
                                                <span className="incident-info-label">Active Personnel: </span>
                                                <span className="incident-info-value">{incidentLocation.activePersonnel}</span>
                                            </div>
                                            <div className="incident-info-row">
                                                <span className="incident-info-label">Incident Duration: </span>
                                                <span className="incident-info-value">{incidentLocation.durationDays} days</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        {selectedLocation?.isIncident ? (
                            <div className="locations-list">
                                <LoadingWrapper
                                    isLoading={!locationsData}
                                    isEmpty={locations.filter(item => !item.isIncident).length === 0}
                                    emptyMessage="No Locations Found for this Incident"
                                >
                                    {locations
                                        .filter(item => !item.isIncident)
                                        .map(item => (
                                            <div
                                                key={item.location.id}
                                                className="location-item"
                                                onClick={() => handleMarkerClick(item)}
                                                onMouseEnter={() => setHoveredLocationId(item.location.id!)}
                                                onMouseLeave={() => setHoveredLocationId(null)}
                                            >
                                                <div className="location-item__section">
                                                    <div>
                                                        <span className="location-item__title">{item.location.name}</span>
                                                    </div>
                                                    <div>
                                                        <span className="location-item__label">Active Personnel: </span>
                                                        <span className="location-item__value">{item.activePersonnel}</span>
                                                    </div>
                                                    <div>
                                                        <span className="location-item__label">Location Active: </span>
                                                        <span className="location-item__value">{item.durationDays} days</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </LoadingWrapper>
                            </div>
                        ) : (
                            <div className="status-map-drawer__location-details">
                                <div>
                                    <span className="location-title">{selectedLocation?.location.name}</span>
                                </div>
                                <div>
                                    <span className="location-label">Active Personnel: </span>
                                    <span className="location-value">{selectedLocation?.activePersonnel}</span>
                                </div>
                                <div>
                                    <span className="location-label">Location Active: </span>
                                    <span className="location-value">{selectedLocation?.durationDays} days</span>
                                </div>
                            </div>
                        )}
                    </DrawerBody>
                </Drawer>
            </div>
        </div>
    );
};

export default StatusMap;